import { useState, useEffect, useRef } from "react";
import { Timer, Calendar, Clock, Download, RefreshCw, Users, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { formatCountdown, getWeekKey, getMonthKey, getNextWeekStart, getNextMonthStart, fmtWeekKey, fmtMonthKey } from "@/lib/chartUtils";

export default function Header({ events, onExport, onRefresh, onRoster }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const wk = getWeekKey(now);
  const mk = getMonthKey(now);
  const nWk = getNextWeekStart(now);
  const nMk = getNextMonthStart(now);
  const wMs = nWk - now;
  const mMs = nMk - now;
  const wEvs = events.filter(e => e.weekKey === wk);
  const mEvs = events.filter(e => e.monthKey === mk);

  return (
    <div data-testid="app-header">
      {/* Top bar */}
      <div className="bg-white/90 backdrop-blur-md border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-600 flex items-center justify-center">
              <Timer className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-extrabold tracking-tight text-zinc-950">CHART NETWORKING</h1>
              <p className="text-xs text-zinc-400 font-body">
                {now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors"
            >
              <Globe className="w-3.5 h-3.5" /> Public Page
            </Link>
            <button
              data-testid="header-roster-btn"
              onClick={onRoster}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors"
            >
              <Users className="w-3.5 h-3.5" /> Roster
            </button>
            <button
              data-testid="refresh-data-btn"
              onClick={onRefresh}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              data-testid="export-csv-btn"
              onClick={onExport}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-zinc-900 hover:bg-zinc-800 rounded-md transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Countdown + Period bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Week countdown */}
          <div data-testid="week-countdown" className="bg-white rounded-lg border border-zinc-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-green-700" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-zinc-400 tracking-widest uppercase">Week Resets</p>
              <p className={`text-lg font-extrabold tracking-tight ${wMs < 3600000 ? 'text-red-600' : 'text-green-700'}`}>
                {formatCountdown(wMs)}
              </p>
              <p className="text-[10px] text-zinc-400">
                Thu {nWk.toLocaleDateString([], { month: "short", day: "numeric" })} @ 5 AM
              </p>
            </div>
          </div>

          {/* Month countdown */}
          <div data-testid="month-countdown" className="bg-white rounded-lg border border-zinc-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-blue-700" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-zinc-400 tracking-widest uppercase">Month Resets</p>
              <p className={`text-lg font-extrabold tracking-tight ${mMs < 86400000 ? 'text-red-600' : 'text-blue-700'}`}>
                {formatCountdown(mMs)}
              </p>
              <p className="text-[10px] text-zinc-400">
                Thu {nMk.toLocaleDateString([], { month: "short", day: "numeric" })} @ 5 AM
              </p>
            </div>
          </div>

          {/* Current week */}
          <div data-testid="current-week-info" className="bg-white rounded-lg border border-zinc-200 p-4 flex items-center gap-3">
            <div className="w-2 h-10 rounded-full bg-green-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-zinc-400 tracking-widest uppercase">Current Week</p>
              <p className="text-sm font-bold text-zinc-950 truncate">{fmtWeekKey(wk)}</p>
              <p className="text-xs text-zinc-400 font-semibold">{wEvs.length} events</p>
            </div>
          </div>

          {/* Current month */}
          <div data-testid="current-month-info" className="bg-white rounded-lg border border-zinc-200 p-4 flex items-center gap-3">
            <div className="w-2 h-10 rounded-full bg-blue-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-zinc-400 tracking-widest uppercase">Current Month</p>
              <p className="text-sm font-bold text-zinc-950 truncate">{fmtMonthKey(mk)}</p>
              <p className="text-xs text-zinc-400 font-semibold">{mEvs.length} events</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
